import hateoas from 'viralstyle-ui/src/hateoas';
import storage from 'viralstyle-ui/src/storage';
import requestService from 'viralstyle-ui/src/requestService';
import pick from 'lodash/pick';
import merge from 'lodash/merge';

import settingsTransformer from './_settings-transformer';

class SettingsApi {
  updateCurrentUser(currentUser) {
    let payload = pick(currentUser, ['public_name', 'name', 'timezone', 'description', 'account_privacy', 'shirt_size', 'notify_campaign', 'notify_payout', 'mobile', 'hide_all_marketplace']);
    payload = { ...payload, ...currentUser.address };

    return requestService.put(hateoas.url('update_client_details', { clientId: storage.get('client-hash') }), payload);
  }

  getSubUsers() {
    return requestService.get(hateoas.url('get_team_members', { clientId: storage.get('client-hash') }));
  }

  saveOrUpdateSubUser(isNew, subUser, currentUserId) {
    let payload,
      props = ['password', 'email', 'role', 'access_domains', 'allowed_domains'];

    if (currentUserId !== subUser.id && subUser.role === 'CLIENT-SUB-ADMIN') {
      payload = pick(subUser, [...props, 'view_payouts', 'request_payouts']);
    } else {
      payload = pick(subUser, props);
    }

    if (isNew) {
      return requestService.post(hateoas.url('create_team_member', { clientId: storage.get('client-hash') }), payload);
    } else {
      return requestService.put(hateoas.url('update_team_member', { clientId: storage.get('client-hash'), teamMemberId: subUser.id }), payload);
    }
  }

  removeSubUser(subUser) {
    return requestService.delete(hateoas.url('delete_team_member', { clientId: storage.get('client-hash'), teamMemberId: subUser.id }))
  }

  getAvailablePayouts(collection) {
    return requestService.get(`${this._payoutsUrl('search_client_payouts', collection)}&sort=${collection.sortColumn}:${collection.sortDirection === 0 ? 'DESC' : 'ASC'}`);
  }

  getRequestedPayouts(collection) {
    return requestService.get(`${this._payoutsUrl('search_client_payout_requests', collection)}&sort=${collection.sortColumn}:${collection.sortDirection === 0 ? 'DESC' : 'ASC'}`);
  }

  getPendingPayouts(collection) {
    return requestService.get(`${this._payoutsUrl('search_pending_client_payouts', collection)}&sort=${collection.sortColumn}:${collection.sortDirection === 0 ? 'DESC' : 'ASC'}`);
  }

  getPayoutDeductions(collection) {
    if (collection.sortColumn) {
      return requestService.get(`${this._payoutsUrl('search_payout_deductions', collection)}&sort=${collection.sortColumn}:${collection.sortDirection === 0 ? 'DESC' : 'ASC'}`);
    } else {
      return requestService.get(this._payoutsUrl('search_payout_deductions', collection));
    }
  }

  getPayoutTotals() {
    return requestService.get(hateoas.url('get_payout_totals', { clientId: storage.get('client-hash') }));
  }

  getReferralTotals() {
    return requestService.get(hateoas.url('client_referral_aggregations', { clientId: storage.get('client-hash') }));
  }

  getAvailableReferrals(collection) {
    return requestService.get(`${this._payoutsUrl('client_referral_search', collection)}&filters[]=available`);
  }

  getRequestedReferrals(collection) {
    return requestService.get(`${this._payoutsUrl('requested_client_referral_search', collection)}&filters[]=active`);
  }

  getPaidReferrals(collection) {
    return requestService.get(`${this._payoutsUrl('requested_client_referral_search', collection)}&filters[]=paid`);
  }

  getCurrentCycleReferrals(collection) {
    return requestService.get(this._payoutsUrl('pending_client_referral_search', collection, { cycle: 'current' }));
  }

  requestReferralPayout(payment_method, payment_info = {}) {
    let payout = {
      payment_method,
      payment_info
    };

    return requestService.post(hateoas.url('request_referral_payout', { clientId: storage.get('client-hash') }), payout);
  }

  updateCurrentUserPassword(current_password, new_password) {
    return requestService.put(hateoas.url('update_client_password', { clientId: storage.get('client-hash') }), { current_password, new_password });
  }

  getPaymentInfo() {
    return requestService.get(hateoas.url('get_client_stored_payment_methods', { clientId: storage.get('client-hash') }));
  }

  getPayoneerLink() {
    return requestService.get(`${hateoas.url('get_new_payoneer_registration_link', { clientId: storage.get('client-hash') })}?redirect_uri=${window.location.pathname}`);
  }

  setDefaultPayoneer(accountId) {
    return requestService.post(hateoas.url('make_payoneer_account_default', { clientId: storage.get('client-hash'), accountId }));
  }

  unlinkPayoneer(accountId) {
    return requestService.delete(hateoas.url('unlink_payoneer_account', { clientId: storage.get('client-hash'), accountId }));
  }

  savePaymentMethod(method, paymentInfo) {
    let payload = settingsTransformer.transformPaymentMethod(paymentInfo, method);

    return requestService.put(hateoas.url('update_client_stored_payment_method', { clientId: storage.get('client-hash'), method }), payload)
  }

  getAweberToken() {
    return requestService.get(`${hateoas.url('get_aweber_auth_url', { clientId: storage.get('client-hash') })}?callback_url=${window.location.href}`);
  }

  saveAweberDetails(params) {
    return requestService.put(hateoas.url('aweber_callback_url', { clientId: storage.get('client-hash') }), params)
  }

  getConstantContactToken() {
    return requestService.get(`${hateoas.url('get_constant_contact_auth_url', { clientId: storage.get('client-hash') })}?callback_url=${window.location.href}`);
  }

  saveConstantContactDetails(params) {
    params.callback_url = window.location.href.split('?')[0];

    return requestService.put(hateoas.url('constant_contact_callback', { clientId: storage.get('client-hash') }), params)
  }

  updateEmailProviderAndApiKey(provider, api_key) {
    return requestService.put(hateoas.url('set_email_provider_api_key', { clientId: storage.get('client-hash') }), { provider, api_key });
  }

  getEmailConfig() {
    return requestService.get(hateoas.url('get_client_email_config', { clientId: storage.get('client-hash') }));
  }

  refreshEmailLists() {
    return requestService.put(hateoas.url('refresh_client_mail_lists', { clientId: storage.get('client-hash') }));
  }

  updateCurrentEmailList(list_id) {
    return requestService.put(hateoas.url('set_default_email_marketing_list', { clientId: storage.get('client-hash') }), { list_id });
  }

  updateClientPixels(user) {
    let payload = settingsTransformer.transformPixels(user);

    return requestService.put(hateoas.url('update_all_pixels', { clientId: storage.get('client-hash') }), payload);
  }

  submitPayoneerPayment(accountId, amount) {
    let payload = {
      amount,
    };

    return requestService.post(hateoas.url('send_payment_via_payoneer', { clientId: storage.get('client-hash'), accountId }), payload);
  }

  _payoutsUrl(endpoint, collection, params = {}) {
    let url = `${hateoas.url(endpoint)}?page={page}&limit={limit}`;

    params = merge(params, { clientId: storage.get('client-hash'), page: collection.page, limit: collection.limit });

    url = url.assignParams(params);

    return url;
  }
}

export default new SettingsApi();